import {
  type CopilotStopResponse,
  copilotConversationResponseSchema,
  copilotCreateResponseSchema,
  copilotStopResponseSchema,
  parseCopilotConversation,
} from "@/features/copilot/schemas/copilot.schema";
import { ApiError, apiCall, resolveApiUrl } from "@/shared/lib/api-call";
import { apiPaths } from "@/shared/lib/api-paths";

export async function getCurrentCopilotConversation(workspaceId: string) {
  const result = await apiCall<unknown>(
    apiPaths.copilot.currentConversation(workspaceId),
    { method: "GET" },
    copilotConversationResponseSchema,
    { strictResponse: true },
  );
  return result === null ? null : parseCopilotConversation(result);
}

export async function createCopilotConversation(workspaceId: string) {
  const result = await apiCall<unknown>(
    apiPaths.copilot.conversations(),
    { method: "POST", body: JSON.stringify({ workspaceId }) },
    copilotCreateResponseSchema,
    { strictResponse: true },
  );
  return parseCopilotConversation(result);
}

export function stopCopilotConversation(conversationId: string): Promise<CopilotStopResponse> {
  return apiCall<CopilotStopResponse>(
    apiPaths.copilot.stop(conversationId),
    { method: "POST" },
    copilotStopResponseSchema,
    { strictResponse: true },
  );
}

export type CopilotStreamFetcher = typeof globalThis.fetch;

export async function throwCopilotStreamError(response: Response): Promise<void> {
  if (response.ok) return;
  const body: unknown = await response
    .clone()
    .json()
    .catch(() => null);
  const envelope = parseCopilotErrorEnvelope(body);
  if (envelope) {
    throw new ApiError({
      status: response.status,
      code: envelope.code,
      message: envelope.message,
      details: envelope.details,
    });
  }
  throw new ApiError({
    status: response.status,
    code: "UNKNOWN",
    message: `HTTP ${response.status}`,
  });
}

function parseCopilotErrorEnvelope(input: unknown): {
  code: string;
  message: string;
  details?: unknown;
} | null {
  if (typeof input !== "object" || input === null || !("error" in input)) return null;
  const error = input.error;
  if (typeof error !== "object" || error === null || !("code" in error) || !("message" in error))
    return null;
  if (typeof error.code !== "string" || typeof error.message !== "string") return null;
  return {
    code: error.code,
    message: error.message,
    ...("details" in error ? { details: error.details } : {}),
  };
}

export const copilotStreamUrl = (conversationId: string) =>
  resolveApiUrl(apiPaths.copilot.messages(conversationId));
